import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", tid: "nav-link-home" },
  { to: "/chi-siamo", label: "Chi siamo", tid: "nav-link-chi-siamo" },
  { to: "/servizi", label: "Servizi", tid: "nav-link-servizi" },
  { to: "/contatti", label: "Contatti", tid: "nav-link-contatti" },
];

const EN_MAP = {
  "/": "/en",
  "/chi-siamo": "/en/about-us",
  "/servizi": "/en/services",
  "/contatti": "/en/contacts",
  "/richiedi-preventivo": "/en/get-a-quote",
  "/privacy-policy": "/en/privacy-policy",
  "/cookie-policy": "/en/cookie-policy",
};
const IT_MAP = {
  "/en": "/",
  "/en/about-us": "/chi-siamo",
  "/en/services": "/servizi",
  "/en/contacts": "/contatti",
  "/en/get-a-quote": "/richiedi-preventivo",
  "/en/privacy-policy": "/privacy-policy",
  "/en/cookie-policy": "/cookie-policy",
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const cur = window.scrollY;
      setScrolled(cur > 40);
      setHidden(!open && cur > 240 && cur > last + 2);
      if (cur < last - 2) setHidden(false);
      last = cur;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
    return () => document.body.classList.remove("menu-open");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 overflow-hidden transition-[background-color,transform] duration-500 ${
          hidden ? "-translate-y-full" : "translate-y-0"
        } ${
          scrolled || open
            ? "bg-ink/90 backdrop-blur-md border-b border-gold/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-5 md:gap-3">
          <Link
            data-testid="nav-brand-logo"
            to="/"
            className="flex min-w-0 flex-col"
          >
            <span className="flex items-center gap-2 md:gap-3">
              <img
                src="/brand/zaichauffer-emblema-trasparente.png"
                alt=""
                className="h-9 w-9 object-contain md:h-12 md:w-12"
              />
              <span className="flex items-center justify-center border-[1.5px] border-gold px-1.5 py-0.5 md:px-2.5">
                <span className="font-serif text-sm font-semibold md:text-xl tracking-[0.14em] text-bone">
                  ZAI
                </span>
              </span>
              <span className="font-serif text-base md:text-2xl tracking-[0.14em] md:tracking-[0.2em] text-bone">
                CHAUFFER
              </span>
            </span>
            <span className="mt-1.5 flex flex-col gap-1">
              <span className="h-px w-full bg-gold/60" />
              <span className="flex justify-between text-[6.5px] uppercase text-gold/90">
                {"Private Chauffeur Service".split("").map((ch, i) => (
                  <span key={i}>{ch === " " ? "\u00A0" : ch}</span>
                ))}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                data-testid={l.tid}
                to={l.to}
                className={({ isActive }) =>
                  `label-cap transition-colors duration-300 hover:text-goldlight ${
                    isActive ? "!text-gold" : "!text-bone/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex flex-none items-center gap-2 md:gap-5">
            {location.pathname.startsWith("/en") ? (
              <Link
                data-testid="lang-switch-it"
                to={IT_MAP[location.pathname] || "/"}
                className="hidden text-[11px] tracking-[0.2em] text-bone/50 transition-colors duration-300 hover:text-gold sm:block"
              >
                IT
              </Link>
            ) : (
              <Link
                data-testid="lang-switch-en"
                to={EN_MAP[location.pathname] || "/en"}
                className="hidden text-[11px] tracking-[0.2em] text-gold transition-colors duration-300 hover:text-goldlight sm:block"
              >
                EN
              </Link>
            )}
            <Link
              data-testid="nav-cta-preventivo"
              to={location.pathname.startsWith("/en") ? "/en/get-a-quote" : "/richiedi-preventivo"}
              className="inline-flex flex-none items-center bg-gold px-2.5 py-2 text-[8px] tracking-[0.12em] uppercase text-ink transition-colors duration-300 hover:bg-goldlight sm:px-5 sm:py-3 sm:text-[10px] sm:tracking-[0.22em]"
            >
              {location.pathname.startsWith("/en") ? (
                <>
                  <span className="sm:hidden">Quote</span>
                  <span className="hidden sm:inline">Request a quote</span>
                </>
              ) : (
                <>
                  <span className="sm:hidden">Preventivo</span>
                  <span className="hidden sm:inline">Richiedi un preventivo</span>
                </>
              )}
            </Link>
            <button
              data-testid="nav-toggle"
              onClick={() => setOpen((v) => !v)}
              className="p-1 text-bone md:p-2 lg:hidden"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-7">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 26, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                >
                  <NavLink
                    data-testid={`nav-mobile-${l.tid}`}
                    to={l.to}
                    className={({ isActive }) =>
                      `font-serif text-4xl ${
                        isActive ? "text-gold" : "text-bone"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ y: 26, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.5 }}
              >
                <Link
                  data-testid="nav-mobile-cta-preventivo"
                  to="/richiedi-preventivo"
                  className="btn-solid mt-4"
                >
                  Richiedi un preventivo
                </Link>
              </motion.div>
            </div>
            <div className="absolute bottom-10 left-8 right-8 flex items-center justify-between">
              <span className="label-cap">Private Chauffeur Service</span>
              {location.pathname.startsWith("/en") ? (
                <Link data-testid="nav-mobile-lang" to={IT_MAP[location.pathname] || "/"} className="text-[11px] tracking-[0.2em] text-bone/50 hover:text-gold">
                  IT
                </Link>
              ) : (
                <Link data-testid="nav-mobile-lang" to={EN_MAP[location.pathname] || "/en"} className="text-[11px] tracking-[0.2em] text-gold hover:text-goldlight">
                  EN
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
